// A translation group as a FILE, so it can be edited somewhere this service is
// not.
//
// The unit is the group and not the page, for the reason
// @common/schema/page/translation gives: a group has one structure and many
// contents, so a file holding one language of it can only ever express half a
// change. Adding an element to `pages[0]` and not to `pages[1]` is not a
// smaller edit, it is an inconsistent one — and the import resolves it the same
// way the editor does, by treating `structureSource` as the page that decides
// what exists and every other page as the language it is written in.
//
// WHAT IT DELIBERATELY DOES NOT CARRY, and why each is a refusal rather than an
// omission that quietly half-works:
//
//   - the site's SECTIONS. A `section` element is a pointer at a row on another
//     table that every page of the site shares, so carrying a copy would let a
//     page bundle rewrite `navbar` for every other page on the site. The import
//     writes no section, and a bundle naming one this site does not have lands
//     as the renderer's "Section not found" — which is what removing a section
//     has always done here.
//   - the site's LOCALES and its translation catalog. Both are the site's, both
//     are edited on the site's own screen, and a page-level file that rewrote
//     them would change every other page in the site as a side effect of
//     importing one.
//   - FILES. The bytes are bucketws', addressed by `${orgId}/${siteId}`, so a
//     URL in this markup is only meaningful inside the site it was exported
//     from. This format is same-site, which is exactly the case where that is
//     not a problem — see `page.import.site.mismatch`.
//
// EVERY PAGE IN HERE IS VALIDATED TWICE, and the second time is the one that
// stays true. This document names the same field definitions /Public/Page/Update
// names — `title`'s `minLength`, `route`'s pattern, `/Page/Element` — so an
// import is refused whole, before the first write, by the assertion on the way
// in. Each page is then validated again as `PageModel.update` writes it, which
// is where every other write of a page is checked and is what keeps this format
// honest if it ever grows a field of its own.
//
// Neither pass reaches the BODY of an element's `config`: `issues` resolves a
// `resolver` union only at the top level of its target, and a union inside an
// array is unchecked in all three places that hold one. See
// api/code/src/validate.
import FieldsSchema, {
  pageId,
  title,
  route,
  locale,
  translationKey,
  elements,
  config,
  editor,
} from "./fields";

// What a reader checks before believing any of the rest.
//
// Two fields rather than one, and neither is decoration: `format` says this is
// a page bundle and not some other JSON somebody dragged onto the screen, and
// `version` is what lets a later shape be REFUSED rather than half-read. They
// are checked in the model rather than as an `enum` here, so that a stale file
// answers "this file was written by a newer pagews" instead of the schema
// layer's "page.import.invalid", which names no field and suggests no fix.
export const FORMAT = "pagews.page-bundle";
export const VERSION = 1;

export default [
  ...FieldsSchema,
  {
    // Which site the file came out of. Both halves, because the partition a
    // page lives in is the composite — see @common/schema/site/key — and a
    // `siteId` alone is a name that repeats across organizations.
    id: "/Page/Bundle/Site",
    type: "object",
    properties: {
      orgId: { type: "string" },
      siteId: { type: "string" },
    },
    required: ["orgId", "siteId"],
  },
  {
    // One language of the page.
    //
    // `pageId` is required and it is the identity — WITHIN THE FILE always,
    // and in the table only for the entity-scoped import. That verb re-imports
    // into the rows the file came from, so the id is the row it names and one
    // the site does not have is refused rather than minted: the id in the file
    // was minted by the server for a row somewhere, and a file that can
    // conjure rows is a file that can conjure them in the wrong site. The
    // listing's create-import never honours the id at all — it mints fresh
    // ones and reads the file's only to join languages to their
    // `structureSource`.
    //
    // Everything else is the row as `/Public/Page/Update` takes it, in the
    // order this file writes it, so that a hand edit lands in the shape the
    // update path already validates.
    id: "/Page/Bundle/Page",
    type: "object",
    properties: {
      pageId,
      title,
      route,
      locale,
      translationKey,
      elements,
      config,
      editor,
    },
    // EVERY FIELD, and that is the difference between this and
    // /Public/Page/Update, where all but `title` are optional and an absent one
    // means "leave it as it was".
    //
    // A screen omits a field because it is a form over part of a page. A FILE
    // omits one because somebody deleted the line — and the reading that is
    // right for a form is catastrophic here: `elements` gone would empty the
    // page and, through the group's one structure, empty every language of it.
    // The export writes all eight, so a bundle missing one has already lost
    // something, and `version` above is what lets the set change later without
    // this becoming a guess.
    required: [
      "pageId",
      "title",
      "route",
      "locale",
      "translationKey",
      "elements",
      "config",
      "editor",
    ],
  },
  {
    id: "/Page/Bundle",
    type: "object",
    properties: {
      format: { type: "string" },
      version: { type: "integer" },
      // Written by the export and read by nobody: it is provenance for the
      // person holding the file, and it is deliberately OUTSIDE the content —
      // export, import and export again produces the same `pages` byte for
      // byte, which is the property that makes a hand edit reviewable as a
      // diff.
      exportedAt: { type: "string" },
      site: { $ref: "/Page/Bundle/Site" },
      // The page whose element list and shell decide what the GROUP has. The
      // export writes the page it was invoked on; an editor who reorders or
      // adds an element does it here and the import gives it to every other
      // language, carrying that language's own prose across — the same
      // `syncedElements` the model applies when the editor saves.
      structureSource: { type: "string" },
      pages: {
        type: "array",
        items: { $ref: "/Page/Bundle/Page" },
        minItems: 1,
      },
    },
    required: ["format", "version", "site", "pages"],
  },
];
